/**
 * Edit Chart Tool (XML-first)
 */

import { ToolBase } from "../../../../base/toolBase";
import { ToolDescription, safeParseToolParams } from "../../../../base/toolDescription";
import { getContextResolver } from "../../../../../core/contextResolver";
import {
    EditChartToolParamsSchema,
    type EditChartToolParams,
    type ExcelReadInfoEntry,
    type EditChartResponse,
} from "./editChart.schema";
import { EXCEL_CHART_XML_REFERENCE } from "./excelChartXml.reference";
import { DEFAULT_EXCEL_API_BASE_URL } from "../../../constants";

const EDIT_CHART_DESCRIPTION = `Create, edit, or delete Excel charts using the XML-first contract.

Actions:
- create: provide full chartXml payload
- edit: provide changes: [{search, replace}] with optional readId lineage
- delete: remove chart by chartId/chartName

Key rules:
- No standalone move action. Placement changes are XML edits in chartXml.
- Use readExcel.readCharts first, then editChart with readId when available.
- chartId/chartName identifies the chart object on a sheet.`;

const EDIT_CHART_SYSTEM_PROMPT = `### editChart
XML-first chart editing for Excel.

Use action=create|edit|delete.

- create requires chartXml
- edit requires chartId|chartName + changes[] (readId optional for lineage)
- delete requires chartId|chartName

${EXCEL_CHART_XML_REFERENCE}`;

export class EditChartTool extends ToolBase<EditChartToolParams, EditChartResponse> {
    public readonly name = "editChart";
    public readonly description: ToolDescription;
    public readonly systemPromptDescription = EDIT_CHART_SYSTEM_PROMPT;

    private sessionId: string | null = null;
    private apiBaseUrl: string;

    fieldsToCheck = [
        { name: "uiDescription", type: "string" as const, list_status: false, order: 0 },
        { name: "sheetName", type: "string" as const, list_status: false, order: 1 },
        { name: "action", type: "string" as const, list_status: false, order: 2 },
        { name: "chartId", type: "string" as const, list_status: false, order: 3 },
        { name: "chartName", type: "string" as const, list_status: false, order: 4 },
        { name: "readId", type: "string" as const, list_status: false, order: 5 },
        { name: "chartXml", type: "string" as const, list_status: false, order: 6 },
        { name: "changes", type: "array" as const, list_status: true, order: 7 },
    ];

    constructor(apiBaseUrl: string = DEFAULT_EXCEL_API_BASE_URL) {
        super();
        this.apiBaseUrl = apiBaseUrl;
        this.description = new ToolDescription(
            "editChart",
            EDIT_CHART_DESCRIPTION,
            EditChartToolParamsSchema
        );
    }

    public setSessionId(sessionId: string): void {
        this.sessionId = sessionId;
    }

    public async execute(parameters: unknown): Promise<EditChartResponse> {
        const parseResult = safeParseToolParams(EditChartToolParamsSchema, parameters);
        if (!parseResult.success) {
            return {
                success: false,
                action: "edit",
                chartId: "",
                error: parseResult.error,
            };
        }

        const params = parseResult.data;
        if (!this.sessionId) {
            return {
                success: false,
                action: params.action,
                chartId: params.chartId || params.chartName || "",
                error: "No session ID set. Parent agent must call setSessionId() first.",
            };
        }

        const locator = params.chartId || params.chartName || "<unspecified>";
        const changesCount = params.changes?.length ?? 0;
        console.log(
            `[EditChartTool] START action=${params.action} sheet=${params.sheetName} ` +
            `chart=${locator} readId=${params.readId ?? "-"} changes=${changesCount}`
        );
        try {
            const normalizedChartXml = this.normalizeChartXmlPayload(params.chartXml);
            const response = await fetch(`${this.apiBaseUrl}/excel/edit-chart`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    session_id: this.sessionId,
                    sheet_name: params.sheetName,
                    action: params.action,
                    chart_id: params.chartId,
                    chart_name: params.chartName,
                    chart_xml: normalizedChartXml,
                    read_id: params.readId,
                    changes: params.changes,
                }),
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error(
                    `[EditChartTool] FAIL action=${params.action} sheet=${params.sheetName} ` +
                    `status=${response.status} error=${errorText}`
                );
                return {
                    success: false,
                    action: params.action,
                    chartId: params.chartId || params.chartName || "",
                    error: `API error: ${response.status} - ${errorText}`,
                };
            }

            const result = await response.json();

            const imageIds: string[] = [];
            const chartImage = result.chartImageBase64 || result.chart_image_base64;
            if (chartImage && typeof chartImage === "string") {
                const contextResolver = getContextResolver();
                const chartLabel = result.chartName || result.chartId || result.chart_id || "chart";
                const imageId = `chart_${String(chartLabel).replace(/[!:\s]/g, "_")}_${Date.now()}`;
                const processed = await contextResolver.storeImageAsync(imageId, chartImage, "image/png");
                imageIds.push(imageId);
                console.log(`[EditChartTool] Stored chart image: ${imageId}${processed.wasResized ? " (resized)" : ""}`);
            }
            const excelReadInfos: ExcelReadInfoEntry[] = [];
            const writeInfo = this.buildChartWriteInfo(params, result as Record<string, unknown>, imageIds);
            if (writeInfo) {
                excelReadInfos.push(writeInfo);
            }

            console.log(
                `[EditChartTool] END action=${params.action} sheet=${params.sheetName} ` +
                `chart=${result.chartId || result.chart_id || locator} success=${Boolean(result.success)} ` +
                `applied=${(result.operations || {}).applied ?? 0} failed=${(result.operations || {}).failed ?? 0}`
            );

            return {
                success: Boolean(result.success),
                action: params.action,
                chartId: result.chartId || result.chart_id || params.chartId || params.chartName || "",
                chartName: result.chartName || result.chart_name,
                message: result.message,
                warnings: result.warnings,
                excelDataSource: result.excelDataSource,
                updatedXml: result.updatedXml,
                chartXml: result.chartXml || result.updatedXml,
                readId: result.readId,
                dataMode: result.dataMode,
                operations: result.operations,
                details: result.details,
                errors: result.errors,
                _imageIds: imageIds.length > 0 ? imageIds : undefined,
                _excelReadInfos: excelReadInfos.length > 0 ? excelReadInfos : undefined,
            };
        } catch (error) {
            const errorMsg = error instanceof Error ? error.message : String(error);
            console.error(
                `[EditChartTool] EXCEPTION action=${params.action} sheet=${params.sheetName} ` +
                `chart=${locator} error=${errorMsg}`
            );
            return {
                success: false,
                action: params.action,
                chartId: params.chartId || params.chartName || "",
                error: errorMsg,
            };
        }
    }

    private normalizeChartRangeForInfo(chartRef?: string | null): string {
        const raw = String(chartRef || "").trim();
        const normalized = raw ? raw.toLowerCase() : "unknown";
        return `CHART:${normalized}`;
    }

    private buildChartWriteInfo(
        params: EditChartToolParams,
        result: Record<string, unknown>,
        imageIds: string[]
    ): ExcelReadInfoEntry | undefined {
        const success = Boolean(result.success);
        if (!success) return undefined;

        const chartRef =
            (result.chartId as string | undefined) ||
            (result.chart_id as string | undefined) ||
            (result.chartName as string | undefined) ||
            (result.chart_name as string | undefined) ||
            params.chartId ||
            params.chartName;
        if (!chartRef) return undefined;

        return {
            track: "content",
            type: "write",
            sheetName: params.sheetName,
            rangeA1: this.normalizeChartRangeForInfo(chartRef),
            detailLevel: params.action === "delete" ? "writeChartDelete" : "writeChart",
            detailRank: 100,
            timestamp: Date.now(),
            imageIds: imageIds.length > 0 ? [...imageIds] : undefined,
        };
    }

    private normalizeChartXmlPayload(chartXml?: string): string | undefined {
        if (typeof chartXml !== "string") return undefined;
        const trimmed = chartXml.trim();
        const cdataPrefix = "<![CDATA[";
        const cdataSuffix = "]]>";
        if (trimmed.startsWith(cdataPrefix) && trimmed.endsWith(cdataSuffix)) {
            console.warn("[EditChartTool] Stripped outer CDATA wrapper from chartXml payload.");
            return trimmed.slice(cdataPrefix.length, -cdataSuffix.length).trim();
        }
        return chartXml;
    }
}

export default EditChartTool;

